import { Box, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import { StarRounded } from "@mui/icons-material";

/**
 * Distribuição das notas de 1 a 5.
 *
 * Hue único de propósito: a nota já está rotulada no eixo, então pintar cada
 * barra de uma cor seria colorir por posição, não por identidade — e a escala
 * vermelho-a-verde leria como semáforo, que aqui é semântica reservada.
 *
 * Os dois tons saem da rampa de alerta do sistema e foram validados contra a
 * superfície de cada modo: o 500 tem contraste de 1.78:1 no claro, baixo demais
 * para uma marca, então o claro usa o 600.
 */
const COR_BARRA = { light: "#9A6200", dark: "#FFB020" };

interface DistribuicaoNotasProps {
    distribuicao: Record<string, number>;
    total: number;
}

export const DistribuicaoNotas = ({ distribuicao, total }: DistribuicaoNotasProps) => {
    const theme = useTheme();
    const cor = COR_BARRA[theme.palette.mode === "dark" ? "dark" : "light"];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {[5, 4, 3, 2, 1].map((nota) => {
                const quantidade = distribuicao?.[String(nota)] ?? 0;
                const proporcao = total > 0 ? quantidade / total : 0;
                const percentual = Math.round(proporcao * 100);

                return (
                    <Tooltip
                        key={nota}
                        title={`${quantidade} ${quantidade === 1 ? "avaliação" : "avaliações"} com nota ${nota} · ${percentual}% do período`}
                        placement="top"
                        arrow
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "default", py: 0.25 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, width: 34, flexShrink: 0 }}>
                                <Typography variant="body2" fontWeight={600} color="text.secondary">
                                    {nota}
                                </Typography>
                                <StarRounded sx={{ fontSize: "0.95rem", color: "text.disabled" }} />
                            </Box>

                            {/* Trilho recessivo + barra de ponta arredondada ancorada na base. */}
                            <Box
                                sx={{
                                    flex: 1,
                                    height: 10,
                                    borderRadius: "4px",
                                    backgroundColor: theme => alpha(theme.palette.text.primary, 0.06),
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: `${proporcao * 100}%`,
                                        height: "100%",
                                        borderRadius: "4px",
                                        backgroundColor: cor,
                                        transition: "width .25s ease",
                                    }}
                                />
                            </Box>

                            {/* Rótulo direto: cinco barras cabem sem virar ruído. */}
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ width: 58, flexShrink: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                            >
                                {quantidade} · {percentual}%
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
};
